import without from 'lodash/without';
import map from 'lodash/map';
import prop from 'lodash/property';
import mapValues from 'lodash/mapValues';
import sortBy from 'lodash/sortBy';
import groupBy from 'lodash/groupBy';
import identity from 'lodash/identity';
import transform from 'lodash/transform';

import { round4 } from 'utilities/math';

import {
  min,
  max,
  mean,
  median,
  quantileSorted,
  standardDeviation,
  sum,
  sampleStandardDeviation
} from 'simple-statistics';

// numerical summary
const getQuantileValues = (values, desiredQuantile) => {
  const sortedValues = sortBy(values);

  const medianIndex = sortedValues.length % 2 !== 0 && Math.floor(sortedValues.length / 2);
  const arrayFirstHalf = medianIndex && sortedValues.slice(0, medianIndex);
  const arraySecondHalf = medianIndex && sortedValues.slice(medianIndex + 1);

  return quantileSorted(medianIndex ? [...arrayFirstHalf, ...arraySecondHalf] : sortedValues, desiredQuantile);
};

const valuesPropsFns = {
  min,
  max,
  mean,
  median,
  standardDeviation,
  sum,
  sampleStandardDeviation,
  firstQuantile: values => getQuantileValues(values, 0.25),
  thirdQuantile: values => getQuantileValues(values, 0.75)
};

export const computeValuesProps = columnValues => {
  const parsedColumnValues = without(columnValues, 'empty', '');
  return mapValues(valuesPropsFns, valuesPropsFn => {
    if (parsedColumnValues.length === 0) {
      return null;
    }
    if (parsedColumnValues.length < 2 &&
        (valuesPropsFn === sampleStandardDeviation ||
           valuesPropsFn === standardDeviation)) {
      return 'N/A';
    }
    return valuesPropsFn(parsedColumnValues);
  }
  );
};

const square = number => number * number;

export const getNumericalSummary = (rows, columns) =>
  columns.reduce((summary, column) => {
    const columnValues = map(rows, prop(column));
    const parsedColumnValues = without(columnValues, 'empty', '');
    const valuesProps = computeValuesProps(parsedColumnValues);
    const squares = map(parsedColumnValues, square);
    const sumOfSquares = sum(squares);

    summary[column] = { name: column, count: parsedColumnValues.length, sumOfSquares, ...valuesProps };
    return summary;
  }, {});


// categorical summary

export const computeLevelsProps = columnValues => {
  const levelsTransformFunctionHandler = (
    levelProps,
    levelValues,
    levelName
  ) => {
    const proportion = levelValues.length / columnValues.length;

    levelProps[levelName] = {
      name: levelName,
      count: levelValues.length,
      proportion: round4(proportion)
    };
  };

  return transform(
    groupBy(columnValues, identity),
    levelsTransformFunctionHandler
  );
};

export const getCategoricalSummary = (rows, columns) => {
  return columns.reduce((summary, column) => {
    const columnValues = map(rows, prop(column));
    const parsedColumnValues = without(columnValues, 'empty', '');

    summary[column] = {
      name: column,
      count: parsedColumnValues.length,
      levels: computeLevelsProps(parsedColumnValues)
    };

    return summary;
  }, {});
};
